import { AlertTriangle, Clock, Mail, ShieldCheck } from "lucide-react";
import PropTypes from "prop-types";
import classnames from "classnames";
import LoadingButton from "@components/ui-components/loading-button";
import StatusChip from "@components/ui-components/status-chip";
import { KYC_LABEL, KYC_TONE } from "@src/utility/constants/panel/common";

const KycVerificationCard = ({ summary, canSendReminder, onRemind, onForceVerify, verifying }) => {
    const { status, hint, daysLeft, expiring, windowDays, windowUsedPct, nameMismatch, canRemind, canForceVerify } = summary;

    return (
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 max-w-xl">
                    <p className="text-xs font-medium uppercase tracking-wider text-muted">KYC status</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        <StatusChip tone={KYC_TONE[status] || "neutral"} label={KYC_LABEL[status] || status} />
                        {daysLeft !== null && (
                            <span className={classnames("inline-flex items-center gap-1 text-xs", expiring ? "font-medium text-danger" : "text-muted")}>
                                {expiring && <AlertTriangle className="h-3.5 w-3.5" />}
                                {daysLeft} day{daysLeft === 1 ? "" : "s"} left in KYC window
                            </span>
                        )}
                    </div>
                    {hint && <p className="mt-2 text-sm text-muted">{hint}</p>}
                </div>

                {(canRemind || canForceVerify) && (
                    <div className="flex shrink-0 items-center gap-2">
                        {canRemind && (
                            <LoadingButton
                                color="neutral"
                                icon={Mail}
                                onClick={onRemind}
                                disabled={!canSendReminder}
                                text="Send reminder" />
                        )}
                        {canForceVerify && (
                            <LoadingButton
                                icon={ShieldCheck}
                                onClick={onForceVerify}
                                loading={verifying}
                                loadingText="Verifying…"
                                text="Force verify" />
                        )}
                    </div>
                )}
            </div>

            {windowUsedPct !== null && (
                <div className="mt-5">
                    <div className="flex items-center justify-between text-xs text-muted">
                        <span className="inline-flex items-center gap-1.5">
                            <Clock className="h-3.5 w-3.5" />
                            KYC window · {windowDays} days
                        </span>
                        <span className={expiring ? "font-medium text-danger" : undefined}>{daysLeft} left</span>
                    </div>
                    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-light">
                        <div className={classnames("h-full rounded-full", expiring ? "bg-danger" : "bg-warning")} style={{ width: `${windowUsedPct}%` }} />
                    </div>
                </div>
            )}

            {canRemind && !canSendReminder && (
                <p className="mt-4 rounded-lg bg-light/60 px-3 py-2 text-xs text-muted">
                    Reminders go out under a brand&apos;s name, and this publisher isn&apos;t linked to a brand yet.
                </p>
            )}

            {nameMismatch && canForceVerify && (
                <p className="mt-4 flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/5 px-3 py-2 text-xs text-gray-700">
                    <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0 text-warning" />
                    The name on the PAN doesn&apos;t match the bank account holder. Check both before force verifying.
                </p>
            )}
        </section>
    );
};

KycVerificationCard.propTypes = {
    summary        : PropTypes.object.isRequired,
    canSendReminder: PropTypes.bool,
    onRemind       : PropTypes.func.isRequired,
    onForceVerify  : PropTypes.func.isRequired,
    verifying      : PropTypes.bool
};

export default KycVerificationCard;
